import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { Button, type ButtonProps } from './button';

export interface PendingButtonProps extends ButtonProps {
  /**
   * While true, the button renders an inline spinner and disables itself. A plain
   * boolean, so it takes a mutation's `isPending` or a query's `isLoading` alike.
   */
  pending?: boolean;
  /**
   * Optional leading icon shown while idle (e.g. a Check). It is swapped for the
   * spinner while pending, so the label never shifts — the dominant "icon → spinner,
   * stable label" shape. Omit it for a plain "prepend a spinner" button.
   */
  icon?: React.ReactNode;
}

/**
 * A button that renders an inline spinner and disables itself while `pending` is
 * true — the "spinner inside a button while working" shape. `aria-busy` announces
 * the in-flight state; the spinner itself is decorative (`aria-hidden`) because the
 * button keeps its text label. The button stays disabled if the caller also passes
 * `disabled`.
 */
export const PendingButton = React.forwardRef<HTMLButtonElement, PendingButtonProps>(
  ({ pending = false, icon, disabled, children, ...props }, ref) => (
    <Button
      ref={ref}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      {...props}
    >
      {pending ? <Loader2 className="animate-spin" aria-hidden="true" /> : icon}
      {children}
    </Button>
  ),
);
PendingButton.displayName = 'PendingButton';
